import {
  setTargetElementClicked,
  removeTargetElementClicked,
  removeSourceElementClicked,
  setSourceElementClicked,
} from 'store/actions/auto-tieout/mapped-elements-actions';
import { scrollElementIntoView } from 'utils/scrolling-utils';
import {
  STATEMENT_ELEMENT_ID_PREFIX,
  STATEMENT_LEFT_ELEMENT_ID_PREFIX,
} from 'constants/feature/statement-content-constants';
import {
  setLoadingOverlay,
  clearLoadingOverlay,
} from 'store/actions/common/loading-overlay-actions';
import { fetchComfortAnnotationListForRevision } from 'store/actions/comfort-annotations-list-actions';
import { fetchTickmarkListForRevision } from 'store/actions/tickmark-list-panel-actions';
import { fetchWorkpaperListForRevision } from 'store/actions/workpaper-toolkit-list-actions';
import { reFetchElementsForSectionsInView } from 'store/actions/statement-content-actions';
import { LEFT_PANELS, RIGHT_PANELS } from 'constants/feature/panel-constants';
import { fetchSelectedElementDetails } from 'store/actions/common/element-panel-actions';
import { updateSelectedBatchElementsFromSocketAction } from 'store/actions/batch-panel-actions';
import { STATEMENT_NAV_TABS } from 'constants/feature/statement-navigator-constants';
import { searchElements } from 'store/actions/statement-navigator/elements-search-panel-actions';
import { fetchStatementSummaryElements } from 'store/actions/statement-summary/elements-summary-actions';
import { setSideBySideSyncScroll } from 'store/actions/side-by-side-statement/side-by-side-statement-actions';
import { isNullOrUndefined } from './object-utils';

export const FETCHING_AUTO_TIEOUT_ELEMENT_DETAILS =
  'statement.content.fetching.auto_tieout_element_details_overlay';
export const FETCHING_TRIAL_BALANCE_AUTO_TIEOUT_ELEMENT_DETAILS =
  'statement.content.fetching.tb.auto_tieout_element_details_overlay';
/**
 * Calculate whether context menu should be shown for an element
 * @param {Object} params - Parameters object
 * @param {Object} params.selectMode - Current select mode object with id property
 * @param {Object} params.workFlowStatus - Workflow status object with isCompleted property
 * @param {boolean} params.isSideBySideView - Whether currently in side-by-side view mode
 * @param {boolean} params.isAutoTieoutMode - Whether currently in auto tieout mode
 * @param {boolean} params.isTrialBalanceMode - Whether currently in trial balance mode
 * @param {boolean} params.isLeftView - Whether this is the left view in side-by-side mode
 * @returns {boolean} True if context menu should be shown, false otherwise
 */
export const shouldShowContextMenu = ({
  selectMode,
  workFlowStatus,
  isSideBySideView,
  isAutoTieoutMode,
  isTrialBalanceMode,
  isLeftView,
  batchElementList,
  elementId,
}) => {
  const isDefaultSelectMode = selectMode?.id === 0;
  const isWorkflowCompleted = workFlowStatus?.isCompleted ?? false;
  const selectedElementIdsInBatch = batchElementList?.selectedElementIds;
  const isAutoTieoutView = isSideBySideView && (isAutoTieoutMode || isTrialBalanceMode);
  const isElementAlreadySelectedInBatch = selectedElementIdsInBatch.indexOf(elementId) >= 0;
  
  if (isAutoTieoutView && isElementAlreadySelectedInBatch) {
    return false; // If element is already selected in batch, do not show context menu to avoid confusion with batch actions
  }

  return (
    isDefaultSelectMode &&
    (!isSideBySideView || // Always show in single statement view
      (isAutoTieoutView && isWorkflowCompleted && !isLeftView)) // Show in auto tieout/trial balance when workflow complete and right side
  );
};

/**
 * Common function to handle loading overlay with minimum display time and refresh panels
 * @param {Object} params - Parameters object
 * @param {Function} params.dispatch - Redux dispatch function
 * @param {Object} params.sectionsInView - Sections currently in view
 * @param {boolean} params.isOCRStatement - Whether current statement is OCR
 * @param {Object} params.revision - Current revision object
 * @param {string} params.left - Left panel type
 * @param {string} params.right - Right panel type
 * @param {Object} params.elementDetails - Current element details
 * @param {string} params.loadingMessage - Loading message to display (default: FETCHING_AUTO_TIEOUT_ELEMENT_DETAILS)
 * @param {number} params.minDisplayTime - Minimum display time in milliseconds (default: 1000)
 */
export const handleLoadingOverlayWithRefresh = async ({
  dispatch,
  sectionsInView,
  isOCRStatement,
  revision,
  left,
  right,
  elementDetails,
  loadingMessage = FETCHING_AUTO_TIEOUT_ELEMENT_DETAILS,
  batchElementList,
  minDisplayTime = 1000,
  elementSearchResults,
  selectedTab,
  elementFilters,
}) => {
  // Set loading overlay
  dispatch(
    setLoadingOverlay({
      isLoading: true,
      loadingMessage,
    }),
  );

  const loadedSectionsInView = sectionsInView.sectionIds;
  const promises = [];

  // Add minimum display time
  const minTimePromise = new Promise((resolve) =>
    setTimeout(resolve, minDisplayTime),
  );

  promises.push(
    dispatch(fetchStatementSummaryElements({ revisionId: revision.id })),
  );

  // Re-fetch elements for sections in view
  if (Array.isArray(loadedSectionsInView) && loadedSectionsInView.length > 0) {
    promises.push(
      dispatch(
        reFetchElementsForSectionsInView(
          loadedSectionsInView,
          isOCRStatement,
          true,
        ),
      ),
    );
  }

  // Update Workpaper elements list if user has the Workpaper panel open
  if (left === LEFT_PANELS.WORKPAPER) {
    promises.push(
      dispatch(
        fetchWorkpaperListForRevision({
          revisionId: revision.id,
        }),
      ),
    );
  }

  // Update Tickmark elements list if user has the Tickmark panel open
  if (left === LEFT_PANELS.TICKMARK) {
    promises.push(
      dispatch(
        fetchTickmarkListForRevision({
          revisionId: revision.id,
        }),
      ),
    );
  }

  // Update Comfort Annotation elements list if user has the Comfort Annotation panel open
  if (left === LEFT_PANELS.COMFORT_ANNOTATION) {
    promises.push(
      dispatch(
        fetchComfortAnnotationListForRevision({
          revisionId: revision.id,
        }),
      ),
    );
  }

  // Update element panel if open
  if (right === RIGHT_PANELS.ELEMENT && elementDetails?.id) {
    promises.push(
      dispatch(
        fetchSelectedElementDetails({
          elementId: elementDetails.id,
        }),
      ),
    );
  }

  // Update element search results if open
  if (
    selectedTab === STATEMENT_NAV_TABS.elements &&
    elementSearchResults.hasElements()
  ) {
    const { searchTerm } = elementFilters.getPageParams();
    dispatch(searchElements({ searchTerm, scrollIntoView: false }));
  }

  promises.push(minTimePromise);

  // Wait for all promises to resolve, then clear loading overlay
  try {
    Promise.all(promises).then((response) => {
      if (right === RIGHT_PANELS.BATCH) {
        const selectedElementsMap = batchElementList?.selectedElementsMap;

        // Handle multiple possible response structures
        let responseData = [];

        // Iterate through all response items to find the data
        for (const item of response) {
          if (item && Array.isArray(item)) {
            // Collect data from array items
            item.forEach((subItem) => {
              if (subItem?.data && Array.isArray(subItem.data)) {
                responseData = [...responseData, ...subItem.data];
              }
            });
          } else if (item?.data) {
            // Handle single response object
            if (Array.isArray(item.data)) {
              responseData = [...responseData, ...item.data];
            } else {
              responseData = item.data;
            }
            break; // Found the data, no need to continue
          }
        }

        if (
          selectedElementsMap &&
          responseData?.length &&
          Object.keys(selectedElementsMap).length > 0
        ) {
          // Create lookup map and process in single pass
          const elementDataMap = new Map(
            responseData.map((elm) => [elm.id, elm]),
          );

          const elementsInSelection = [];
          for (const element of Object.values(selectedElementsMap)) {
            const elementData = elementDataMap.get(element?.id);
            if (elementData) {
              elementsInSelection.push(elementData);
            }
          }

          if (elementsInSelection.length > 0) {
            // update the batch panel selected elements from API response
            dispatch(
              updateSelectedBatchElementsFromSocketAction(elementsInSelection),
            );
          }
        }
      }
      dispatch(clearLoadingOverlay());
    });
  } catch (error) {
    console.error('Error in handleLoadingOverlayWithRefresh:', error);
    dispatch(clearLoadingOverlay()); // Clear overlay even on error
  }
};

/**
 * Common function to handle auto tieout element click functionality
 * @param {Object} params - Parameters object
 * @param {string} params.elementId - The element ID that was clicked
 * @param {boolean} params.isLeftView - Whether this is the left view
 * @param {boolean} params.isAutoTieoutMode - Whether auto tieout mode is active
 * @param {Object} params.autoTieoutElementData - Auto tieout element data object
 * @param {Object} params.sectionsInView - Sections in view for right side
 * @param {Object} params.leftSectionsInView - Sections in view for left side
 * @param {boolean} params.selectedStatementIsOCR - Whether selected statement is OCR
 * @param {boolean} params.leftStatementIsOCR - Whether left statement is OCR
 * @param {Function} params.dispatch - Redux dispatch function
 * @returns {void}
 */
export const handleAutoTieoutElementClick = ({
  elementId,
  isLeftView,
  isAutoTieoutMode,
  autoTieoutElementData,
  sectionsInView,
  leftSectionsInView,
  selectedStatementIsOCR,
  leftStatementIsOCR,
  dispatch,
}) => {
  if (!autoTieoutElementData) return;

  const sourceElementClicked = autoTieoutElementData.getSourceElementClicked();
  const targetElementClicked = autoTieoutElementData.getTargetElementClicked();

  const elementClickedId = sourceElementClicked
    ? sourceElementClicked?.sourceElementId
    : targetElementClicked?.targetElementId;

  // If clicking the same element, deselect it
  if (!isNullOrUndefined(elementClickedId) && elementId == elementClickedId) {
    dispatch(removeSourceElementClicked());
    dispatch(removeTargetElementClicked());
    return;
  }

  const elementClicked = isLeftView
    ? autoTieoutElementData.getSourceMapping(elementId)
    : autoTieoutElementData.getTargetMapping(elementId);

  const scrollToStatementElementId = isLeftView
    ? autoTieoutElementData.getTargetElementForSource(elementId)
    : autoTieoutElementData.getSourceElementForTarget(elementId);

  const sectionId = isLeftView
    ? autoTieoutElementData.getTargetSectionIdForSourceElement(elementId)
    : autoTieoutElementData.getSourceSectionIdForTargetElement(elementId);

  const isActive = isLeftView
    ? autoTieoutElementData.isSourceElementActive(elementId)
    : autoTieoutElementData.isTargetElementActive(elementId);

  if (isAutoTieoutMode && elementClicked && isActive) {
    // Set the clicked element
    if (isLeftView) {
      dispatch(removeTargetElementClicked());
      dispatch(setSourceElementClicked(elementClicked));
    } else {
      dispatch(removeSourceElementClicked());
      dispatch(setTargetElementClicked(elementClicked));
    }

    // Scroll to the corresponding element
    const elementIdPrefix = isLeftView
      ? STATEMENT_ELEMENT_ID_PREFIX
      : STATEMENT_LEFT_ELEMENT_ID_PREFIX;

    const sectionsView = isLeftView ? sectionsInView : leftSectionsInView;
    const isOCR = isLeftView ? selectedStatementIsOCR : leftStatementIsOCR;

    // In Auto Tieout mode, disable sync scroll when a mapped element is clicked
    dispatch(setSideBySideSyncScroll(false));

    scrollElementIntoView({
      elementIdPrefix,
      elementId: scrollToStatementElementId,
      sectionId: sectionId,
      sectionsInView: sectionsView,
      isOCR,
      isLeftView,
      isAutoTieoutMode,
    });
  }
};

export const handleTBAutoTieoutElementClick = ({
  rowElementsData,
  isTrialBalanceCompleted,
  mappedElements,
  sectionsInView,
  isOCR,
}) => {
  if (!isTrialBalanceCompleted) return;

  if (mappedElements.isLoaded) {
    const { elementId, sectionId, active } = rowElementsData;
    if (active && elementId && sectionId) {
      scrollElementIntoView({
        elementId,
        sectionId,
        sectionsInView,
        isOCR,
        isLeftView: false,
      });
    }
  }
};
